import React from "react";
import type { PredictionResponse } from "@/services/predictionService";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { useTheme } from "@/context/ThemeContext";

interface Props {
  prediction: PredictionResponse;
  envData?: any;
  timeframe?: "5y" | "10y";
}

// Render four separate charts (with X/Y axes) for each KPI
const PredictionTrends: React.FC<Props> = ({
  prediction,
  envData,
  timeframe = "5y",
}) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const numYears = timeframe === "5y" ? 5 : 10;
  const years = Array.from(
    { length: numYears + 1 },
    (_, i) => new Date().getFullYear() + i,
  );

  const buildSeries = (
    current: number,
    projected: number,
    confidence = 0.5,
  ) => {
    const steps = years.length - 1;
    return years.map((year, i) => {
      const progress = i / steps;
      const value = current + (projected - current) * progress;
      const uncertainty = (1 - confidence) * Math.max(1, Math.abs(value)) * 0.3;
      return {
        year: String(year),
        value: Number(value.toFixed(3)),
        lower: Number(Math.max(0, value - uncertainty).toFixed(3)),
        upper: Number((value + uncertainty).toFixed(3)),
      };
    });
  };

  const currentDef = envData?.deforestation?.ratePerYear ?? 0;
  const predDef = prediction?.predictions?.deforestation_rate_5y ?? 0;
  const defConf = prediction?.predictions?.confidence?.deforestation ?? 0.5;
  const defData = buildSeries(currentDef, predDef, defConf);

  const currentLand = envData?.landLoss?.ratePerYear ?? 0;
  const predLand = prediction?.predictions?.land_loss_5y ?? 0;
  const landConf = prediction?.predictions?.confidence?.land_loss ?? 0.5;
  const landData = buildSeries(currentLand, predLand, landConf);

  const currentDes = envData?.desertification?.ratePerYear ?? 0;
  const predDes = prediction?.predictions?.desertification_area_5y ?? 0;
  const desConf = prediction?.predictions?.confidence?.desertification ?? 0.5;
  const desData = buildSeries(currentDes, predDes, desConf);

  const currentSpec = envData?.endangeredSpecies?.count ?? 0;
  const predSpec = prediction?.predictions?.species_impact_5y ?? 0;
  const specConf = prediction?.predictions?.confidence?.species ?? 0.5;
  const specData = buildSeries(currentSpec, predSpec, specConf);

  const ChartCard = ({
    metric,
    title,
    subtitle,
    data,
    color,
    unit,
  }: {
    metric: string;
    title: string;
    subtitle: string;
    data: any[];
    color: string;
    unit?: string;
  }) => (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <div className="text-xs text-muted-foreground">{subtitle}</div>
      </CardHeader>
      <CardContent>
        <div className="w-full h-[180px] ml-auto mr-auto">
          <ResponsiveContainer>
            <LineChart
              data={data}
              margin={{ top: 20, right: 0, left: 0, bottom: 8 }}
            >
              <defs>
                <linearGradient
                  id={`grad-${metric}`}
                  x1="0"
                  y1="0"
                  x2="1"
                  y2="0"
                >
                  <stop offset="0%" stopColor={color} />
                  <stop offset="50%" stopColor={color} stopOpacity={0.9} />
                  <stop offset="100%" stopColor={color} stopOpacity={0.7} />
                </linearGradient>
                <linearGradient
                  id={`area-${metric}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={color} stopOpacity={0.12} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                stroke={isDark ? "#374151" : "#e5e7eb"}
                strokeDasharray="3 3"
                vertical={false}
              />
              <XAxis
                dataKey="year"
                stroke={isDark ? "#9ca3af" : "#6b7280"}
                tick={{
                  fontWeight: 600,
                  fontSize: 12,
                  fontFamily: "Inter, sans-serif",
                }}
                axisLine={false}
                tickLine={false}
                tickMargin={8}
              />
              <YAxis
                stroke={isDark ? "#9ca3af" : "#6b7280"}
                tick={{
                  fontWeight: 600,
                  fontSize: 12,
                  fontFamily: "Inter, sans-serif",
                }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v: any) => {
                  if (unit === "%") return `${v}%`;
                  if (unit === "km²")
                    return `${Number(v).toLocaleString()} km²`;
                  if (unit === "species") return `${Math.round(v)}`;
                  return `${v}`;
                }}
              />
              <Tooltip
                contentStyle={
                  isDark
                    ? {
                        backgroundColor: "#18181b",
                        border: "1px solid #27272a",
                        borderRadius: 10,
                        color: "#fff",
                        fontFamily: "Inter, sans-serif",
                        fontWeight: 600,
                      }
                    : {
                        backgroundColor: "#fff",
                        border: "1px solid #e5e7eb",
                        borderRadius: 10,
                        color: "#222",
                        fontFamily: "Inter, sans-serif",
                        fontWeight: 600,
                      }
                }
                labelStyle={
                  isDark
                    ? { fontWeight: 700, fontSize: 13, color: "#fff" }
                    : { fontWeight: 700, fontSize: 13, color: "#222" }
                }
                formatter={(value: any, name: string) => {
                  if (unit === "%") return [`${value}%`, name];
                  if (unit === "km²")
                    return [`${Number(value).toLocaleString()} km²`, name];
                  if (unit === "species")
                    return [`${Math.round(value)} species`, name];
                  return [value, name];
                }}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke={`url(#grad-${metric})`}
                strokeWidth={3}
                dot={false}
                activeDot={{ r: 6, stroke: color, strokeWidth: 2, fill: color }}
              />
              <Line
                type="monotone"
                dataKey="upper"
                stroke={color}
                strokeOpacity={0.18}
                strokeDasharray="4 6"
              />
              <Line
                type="monotone"
                dataKey="lower"
                stroke={color}
                strokeOpacity={0.18}
                strokeDasharray="4 6"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-2 text-sm text-muted-foreground">
          Projected endpoint:{" "}
          <span className="font-medium">
            {data[data.length - 1]?.value?.toFixed
              ? data[data.length - 1].value.toFixed(2)
              : data[data.length - 1].value}
            {unit ?? ""}
          </span>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ChartCard
          metric="deforestation"
          title="Forests (Deforestation rate)"
          subtitle={`Projected ${timeframe} trend`}
          data={defData}
          color="#40E0D0"
          unit="%"
        />
        <ChartCard
          metric="landLoss"
          title="Land Loss (area)"
          subtitle={`Projected ${timeframe} trend`}
          data={landData}
          color="#6366f1"
          unit="km²"
        />
        <ChartCard
          metric="desertification"
          title="Desertification (area)"
          subtitle={`Projected ${timeframe} trend`}
          data={desData}
          color="#f59e0b"
          unit="km²"
        />
        <ChartCard
          metric="species"
          title="Species Impact (estimated count)"
          subtitle={`Projected ${timeframe} trend`}
          data={specData}
          color="#ef4444"
          unit="species"
        />
      </div>
    </div>
  );
};

export default PredictionTrends;
